import {View} from 'react-native'
import {plural} from '@lingui/core/macro'
import {Trans, useLingui} from '@lingui/react/macro'

import {type DocumentMaterial} from '#/lib/document/material'
import {
  buildDocumentLeaderboard,
  buildDocumentReport,
  type DocumentLeaderboardEntry,
  documentLeaderboardRows,
  LEADERBOARD_TOP,
} from '#/lib/document/report'
import {useDocumentMaterialQuery} from '#/state/queries/document-material'
import {useDocumentPlaysQuery} from '#/state/queries/document-plays'
import {PreviewableUserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {Button} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

/**
 * Who has played a document, and — for its author — how it is going.
 *
 * One card, two readers, because both are answered by the same plays. A reader
 * opens it to see where they would stand among people they know, which is the
 * only reason a leaderboard is worth opening at all; the author opens it to see
 * *is this deck working*, and gets three numbers and a short list of the pages that
 * catch people out. Anything longer would be a dashboard, and a dashboard behind a
 * chip on a page is a thing nobody opens twice.
 *
 * The report is drawn from the plays the AppView lists, and the material is fetched
 * here — on open — so the per-page table can say how many questions a page asks
 * without the reader's own page-table request having been spent on a summary nobody
 * may look at. A deployment without the method reports an empty board rather than an
 * error: the feature switches itself on when the backend that implements it ships,
 * and the card that opens this is the one thing that should never throw.
 */

/** Gold, silver, bronze — in that order, for the three places that get a colour. */
const MEDALS = ['#F2C14E', '#C9CFD6', '#D9A06B']

export function DocumentPlaysDialog({
  control,
  documentUri,
  title,
  materialUri,
  pageTable,
  pageCount,
  enabled,
  isAuthor = false,
  viewerDid,
  onPlay,
}: {
  control: Dialog.DialogControlProps
  documentUri: string | undefined
  title?: string
  /** Where the document's material lives, for the per-page question counts. */
  materialUri?: string
  /**
   * What each page holds, from the post's own table.
   *
   * The board is ranked on progress *and* score, so it needs to know how many of the
   * document's pages ask something — and the fetched material only answers that once
   * it has arrived (or never, if the fetch fails). The table is carried by the post,
   * so passing it through means the board the author opens names the same reader #1 as
   * the card's own strip did, whatever the network did in between.
   */
  pageTable?: {pages: {page: number; questions: number}[]} | null
  /**
   * How long the document is, in pages, when the caller knows.
   *
   * What each read's progress is measured against: "12 of 16 pages" is the sentence
   * the reader's own callout says when it offers to carry on, so the author's board
   * and the reader's card are counting the same thing. Absent means nobody knows the
   * document's length — the rows then say how many pages a read got through without a
   * denominator, rather than against one nobody can trust.
   */
  pageCount?: number
  /**
   * Whether the summary has actually been asked for.
   *
   * It has to be said out loud rather than read off the control: a dialog is
   * mounted with the card that opens it — on native, before it is ever
   * presented — and `control.isOpen` is only a web-side convenience that a
   * native control does not carry at all. A caller that knows it has just
   * opened this is the one signal that means the same thing on both.
   */
  enabled: boolean
  /** Whether the reader wrote the document, and so sees its numbers. */
  isAuthor?: boolean
  /** Who is reading, so the board can mark their own line and the people they know. */
  viewerDid?: string
  /**
   * Plays the document, for the one press an empty board offers.
   *
   * Absent when the document asks nothing, which is exactly when there would be
   * nothing to play.
   */
  onPlay?: () => void
}) {
  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Inner
        documentUri={documentUri}
        title={title}
        materialUri={materialUri}
        pageTable={pageTable}
        pageCount={pageCount}
        enabled={enabled}
        isAuthor={isAuthor}
        viewerDid={viewerDid}
        onPlay={onPlay}
      />
    </Dialog.Outer>
  )
}

function Inner({
  documentUri,
  title,
  materialUri,
  pageTable,
  pageCount,
  enabled,
  isAuthor,
  viewerDid,
  onPlay,
}: {
  documentUri: string | undefined
  title?: string
  materialUri?: string
  pageTable?: {pages: {page: number; questions: number}[]} | null
  pageCount?: number
  enabled: boolean
  isAuthor: boolean
  viewerDid?: string
  onPlay?: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const query = useDocumentPlaysQuery(documentUri, {enabled})
  const materialQuery = useDocumentMaterialQuery(materialUri, {enabled})
  const material: DocumentMaterial | undefined = materialQuery.data ?? undefined
  const plays = query.data ?? []
  const report = buildDocumentReport({plays, material})
  const board = buildDocumentLeaderboard({
    plays,
    material,
    pageTable,
    pageCount,
    viewerDid,
  })
  const rows = documentLeaderboardRows(board, {top: LEADERBOARD_TOP})

  return (
    <Dialog.ScrollableInner
      label={l`Document plays`}
      style={[a.w_full, {maxWidth: 480}]}>
      <View style={[a.gap_lg, a.pt_md]} testID="documentPlaysDialog">
        <View style={[a.gap_xs]}>
          <Text style={[a.text_lg, a.font_bold]}>
            {isAuthor ? (
              <Trans>How this document is going</Trans>
            ) : (
              <Trans>This document's leaderboard</Trans>
            )}
          </Text>
          {title && (
            <Text
              style={[a.text_sm, t.atoms.text_contrast_medium]}
              numberOfLines={1}>
              {title}
            </Text>
          )}
        </View>

        {query.isLoading ? (
          <View style={[a.align_center, a.py_lg]}>
            <Loader size="md" />
          </View>
        ) : query.isError ? (
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            <Trans>Could not load the plays right now.</Trans>
          </Text>
        ) : board.length === 0 ? (
          <EmptyBoard isAuthor={isAuthor} onPlay={onPlay} />
        ) : (
          <>
            {/*
              The author's numbers, and only the author's: a reader came here for
              the board, and an average nobody else can act on is noise on the way
              to it.
            */}
            {isAuthor && (
              <View style={[a.flex_row, a.gap_sm]}>
                <Stat
                  label={l`Plays`}
                  value={String(report.plays)}
                  sub={l`${plural(report.players, {
                    one: '# reader',
                    other: '# readers',
                  })}`}
                />
                <Stat
                  label={l`Average`}
                  value={
                    report.averagePct === null ? '—' : `${report.averagePct}%`
                  }
                  sub={
                    report.bestPct === null
                      ? undefined
                      : l`best ${report.bestPct}%`
                  }
                />
                <Stat
                  label={l`Time`}
                  value={formatMinutes(report.totalTimeMs)}
                  sub={l`in total`}
                />
              </View>
            )}

            <View style={[a.gap_sm]} testID="documentLeaderboard">
              {/*
                One label for both readers of this card: a reader does not need
                "Leaderboard" twice over — the heading above already says it is
                this document's — and the list is the top of the board plus their
                own neighbours either way.
              */}
              <Text style={[a.text_md, a.font_bold]}>
                <Trans>Top readers</Trans>
              </Text>
              {rows.map((row, index) =>
                row.kind === 'gap' ? (
                  <View key={`gap-${index}`} style={[a.align_center]}>
                    <Text style={[a.text_2xs, t.atoms.text_contrast_medium]}>
                      {l`… ${plural(row.count, {
                        one: '# more reader',
                        other: '# more readers',
                      })}`}
                    </Text>
                  </View>
                ) : (
                  <ReaderRow key={row.entry.playerDid} entry={row.entry} />
                ),
              )}
            </View>

            {isAuthor && report.pages.length > 0 && (
              <View style={[a.gap_sm]}>
                <Text style={[a.text_md, a.font_bold]}>
                  <Trans>Pages to look at again</Trans>
                </Text>
                {report.missedPages.length === 0 ? (
                  <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                    <Trans>
                      Nothing answered yet has been missed — every page answered
                      so far has been got right.
                    </Trans>
                  </Text>
                ) : (
                  report.missedPages.slice(0, 6).map(page => (
                    <View
                      key={page.page}
                      style={[a.flex_row, a.align_center, a.gap_sm]}>
                      <View
                        style={[
                          a.px_xs,
                          a.py_2xs,
                          a.rounded_xs,
                          t.atoms.bg_contrast_50,
                        ]}>
                        <Text style={[a.text_2xs, a.font_bold]}>
                          <Trans>Page {page.page}</Trans>
                        </Text>
                      </View>
                      <Text
                        style={[a.text_sm, a.flex_1, t.atoms.text]}
                        numberOfLines={1}>
                        {l`${plural(page.misses, {
                          one: '# answer wrong',
                          other: '# answers wrong',
                        })}`}
                        {page.pct !== null ? ` · ${page.pct}% correct` : ''}
                      </Text>
                    </View>
                  ))
                )}
              </View>
            )}
          </>
        )}
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}

/**
 * A board with nobody on it yet.
 *
 * Not an error, and not a dead end: a document whose questions nobody has
 * answered is a document waiting for its first reader, and the card says so with
 * the one control that changes it.
 */
function EmptyBoard({
  isAuthor,
  onPlay,
}: {
  isAuthor: boolean
  onPlay?: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  return (
    <View style={[a.gap_sm]}>
      <View
        style={[
          a.gap_xs,
          a.p_md,
          a.rounded_sm,
          a.border,
          t.atoms.border_contrast_low,
        ]}>
        <Text style={[a.text_md, a.font_bold]}>
          <Trans>No one has taken its questions yet</Trans>
        </Text>
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {isAuthor ? (
            <Trans>
              Plays land here as readers answer the questions on its pages — the
              first one takes the top of the board.
            </Trans>
          ) : (
            <Trans>Be the first, and the top of the board is yours.</Trans>
          )}
        </Text>
      </View>
      {onPlay && (
        <View style={[a.flex_row]}>
          <Button
            label={l`Play this document's questions`}
            onPress={onPlay}
            size="small"
            variant="solid"
            color="primary"
            shape="default">
            <Text style={[a.text_sm, a.font_bold, {color: '#fff'}]}>
              <Trans>Play it yourself</Trans>
            </Text>
          </Button>
        </View>
      )}
    </View>
  )
}

/**
 * One reader's line: their place, their face, and how they did.
 *
 * The order is the order the board is ranked in — place, then person, then what
 * they scored — so a column of reads can be scanned down either the places or the
 * names without the two fighting each other.
 */
function ReaderRow({entry}: {entry: DocumentLeaderboardEntry}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const name = entry.profile.displayName?.trim() || entry.handle || l`A reader`
  const tie = entry.followsYou
    ? entry.youFollow
      ? l`Following each other`
      : l`Follows you`
    : entry.youFollow
      ? l`Following`
      : undefined

  return (
    <View
      style={[
        a.flex_row,
        a.align_center,
        a.gap_sm,
        a.px_xs,
        a.py_xs,
        a.rounded_sm,
        entry.isViewer && t.atoms.bg_contrast_25,
      ]}>
      <RankChip rank={entry.rank} />
      <PreviewableUserAvatar size={36} profile={entry.profile} />
      <View style={[a.flex_1]}>
        <Text numberOfLines={1} style={[a.text_sm, a.font_bold, t.atoms.text]}>
          {name}
          {entry.isViewer && (
            <Text
              style={[a.text_sm, a.font_bold, {color: t.palette.primary_500}]}>
              {l` (you)`}
            </Text>
          )}
        </Text>
        <Text
          numberOfLines={1}
          style={[a.text_2xs, t.atoms.text_contrast_medium]}>
          {[
            entry.handle ? `@${entry.handle}` : null,
            tie,
            plural(entry.answers, {one: '# answer', other: '# answers'}),
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </View>
      {/*
        What the reader did *and* how much of the document they did it on, one above
        the other: the board is ordered on the two together, so a row that showed only
        the score (or only "12 pages read", which says nothing about how many there
        are to get through) would leave the reader guessing at why they are where they
        are. The pair is the two halves of the key — the percent, and the share of the
        document the read was made on (`weighted`).
      */}
      <View style={[a.align_end]}>
        <Text style={[a.text_sm, a.font_bold, t.atoms.text]}>
          {entry.pct === null ? '—' : `${entry.pct}%`}
        </Text>
        <Text style={[a.text_2xs, t.atoms.text_contrast_medium]}>
          {documentLengthOf(entry) > 0
            ? l`${entry.pagesRead} of ${documentLengthOf(entry)} pages`
            : plural(entry.pagesRead, {
                one: '# page read',
                other: '# pages read',
              })}
        </Text>
      </View>
    </View>
  )
}

/**
 * How long the document is, as the board knows it — otherwise nought.
 *
 * Nought means "nobody knows", which is a board built without a length, a material or
 * a page table to read one from, and it is the case where the row falls back to the
 * bare count: "0 of 0 pages" is not a sentence worth printing, and neither is a share
 * of a length nobody has counted.
 */
function documentLengthOf(entry: DocumentLeaderboardEntry): number {
  return entry.progressPages && entry.progressPages > 0
    ? entry.progressPages
    : 0
}

/**
 * A place, as a chip: gold, silver and bronze for the podium, a plain count for
 * everyone behind it.
 *
 * The colours are the point of it — "who is winning" is the one thing a reader
 * looks for first, and a row of identical grey numbers makes them read every line
 * to find it.
 */
function RankChip({rank}: {rank: number}) {
  const t = useTheme()
  const medal = MEDALS[rank - 1]
  return (
    <View
      style={[
        a.align_center,
        a.justify_center,
        {
          width: 26,
          height: 26,
          borderRadius: 13,
          backgroundColor: medal ? `${medal}44` : t.palette.contrast_50,
          borderWidth: medal ? 1 : 0,
          borderColor: medal,
        },
      ]}>
      <Text
        style={[
          a.text_xs,
          a.font_bold,
          {color: medal ? MEDAL_INK : t.atoms.text.color},
        ]}>
        {rank}
      </Text>
    </View>
  )
}

/** Dark enough to read on a medal tint, in either theme. */
const MEDAL_INK = '#3a2f10'

function Stat({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub?: string
}) {
  const t = useTheme()
  return (
    <View
      style={[
        a.flex_1,
        a.gap_2xs,
        a.p_md,
        a.rounded_sm,
        a.border,
        t.atoms.border_contrast_low,
        t.atoms.bg_contrast_25,
      ]}>
      <Text
        style={[
          a.text_2xs,
          a.font_bold,
          t.atoms.text_contrast_medium,
          {letterSpacing: 0.6, textTransform: 'uppercase'},
        ]}>
        {label}
      </Text>
      <Text style={[a.text_xl, a.font_bold]}>{value}</Text>
      {sub && (
        <Text style={[a.text_2xs, t.atoms.text_contrast_medium]}>{sub}</Text>
      )}
    </View>
  )
}

/** Time spent, to the minute — a report is not a stopwatch. */
function formatMinutes(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—'
  const minutes = Math.round(ms / 60000)
  if (minutes < 1) return '<1m'
  return `${minutes}m`
}
